import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Bell, AlertTriangle, XCircle, ChevronRight, Settings } from "lucide-react";
import { useAuth } from "@/App";
import { can } from "@/lib/permissions";
import { useTestMode } from "@/components/TestEnvBanner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TopBar = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const showSettings = can(user, "settings", "master");
  const testMode = useTestMode();

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/alerts`, { withCredentials: true });
      setAlerts(res.data.alerts || []);
      setTotalCount(res.data.total_count || 0);
    } catch (error) {
      // Silently fail - alerts are not critical
      setAlerts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch alerts on mount and route change
  useEffect(() => {
    fetchAlerts();
  }, [location.pathname]);

  // Background polling every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAlerts();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Handle bell click - refetch when opening
  const handleBellClick = () => {
    if (!dropdownOpen) {
      fetchAlerts();  // refetch when opening
    }
    setDropdownOpen(!dropdownOpen);
  };

  // Group alerts by category
  const groupedAlerts = alerts.reduce((acc, alert) => {
    const cat = alert.category || "Other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(alert);
    return acc;
  }, {});

  const getSeverityIcon = (severity) => {
    if (severity === "error") {
      return <XCircle className="w-4 h-4 text-red-500 shrink-0" strokeWidth={1.5} />;
    }
    return <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" strokeWidth={1.5} />;
  };

  const handleAlertClick = (link) => {
    setDropdownOpen(false);
    if (link) {
      navigate(link);
    }
  };

  return (
    <div className={`h-14 bg-white border-b border-gray-200 flex items-center justify-end px-6 sticky ${testMode ? "top-7" : "top-0"} z-30`}>
      {/* Left side - can add breadcrumbs or other content here */}
      {children && (
        <div className="flex items-center gap-2 mr-auto">
          {children}
        </div>
      )}

      {/* Right side - Settings (master only) and Bell icons */}
      {showSettings && (
      <button 
        onClick={() => navigate("/settings")}
        className="p-2 hover:bg-gray-100 rounded-full transition-colors cursor-pointer mr-2"
        aria-label="Settings"
        data-testid="settings-icon"
      >
        <Settings className="w-5 h-5 text-gray-700" strokeWidth={1.5} />
      </button>
      )}

      <div className="relative mr-2">
        <DropdownMenu open={dropdownOpen} onOpenChange={(open) => {
          if (open && !dropdownOpen) {
            fetchAlerts();  // refetch when opening
          }
          setDropdownOpen(open);
        }}>
          <DropdownMenuTrigger asChild>
            <button
              className="p-2 hover:bg-gray-100 rounded-full transition-colors cursor-pointer relative"
              data-testid="bell-icon"
            >
              <Bell className="w-5 h-5 text-gray-700" strokeWidth={1.5} />
              {totalCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-semibold rounded-full flex items-center justify-center"
                  data-testid="alert-badge"
                >
                  {totalCount > 99 ? "99+" : totalCount}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-80 max-h-96 overflow-y-auto p-0 rounded-sm"
            data-testid="alerts-dropdown"
          >
            {/* Header */}
            <div className="p-3 border-b border-gray-100 bg-gray-50">
              <h3 className="font-bold text-sm uppercase tracking-wider">
                Alerts {totalCount > 0 && <span className="text-gray-500">({totalCount})</span>}
              </h3>
            </div>

            {/* Content */}
            {loading ? (
              <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
            ) : alerts.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                No alerts at this time
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {Object.entries(groupedAlerts).map(([category, categoryAlerts]) => (
                  <div key={category}>
                    {/* Category header */}
                    <div className="px-3 py-2 bg-gray-50 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                      {category}
                    </div>
                    {/* Alerts in category */}
                    {categoryAlerts.map((alert) => (
                      <div
                        key={alert.id}
                        onClick={() => handleAlertClick(alert.link)}
                        className="w-full px-3 py-2.5 flex items-start gap-2 hover:bg-gray-50 transition-colors text-left cursor-pointer"
                        data-testid={`alert-${alert.id}`}
                      >
                        {getSeverityIcon(alert.severity)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium truncate">{alert.title}</span>
                            <span className="text-xs text-gray-500 shrink-0">{alert.count}</span>
                          </div>
                          <p className="text-xs text-gray-500 truncate">{alert.message}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" strokeWidth={1.5} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default TopBar;
